import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import MainLayOut from '../../layout/MainLayOut';
import { useAuth } from './AuthContext';
import ShippingAddress from './ShippingAddress';
import Myorders from './MyOrders';
import OrderDetails from './OrderDetails';
import MyProfile from './MyProfile';
import OverView from './OverView';
import BottomFixedNav from '../mobileComponents/BottomFixedNav';
import { ToastContainer } from 'react-toastify';
import {
  FiGrid,
  FiPackage,
  FiMapPin,
  FiUser,
  FiLogOut,
  FiChevronLeft,
} from 'react-icons/fi';

const NAV_ITEMS = [
  { label: 'Overview', path: '/myaccount', icon: FiGrid },
  { label: 'My Orders', path: '/myaccount/orders', icon: FiPackage },
  { label: 'My Addresses', path: '/myaccount/addresses', icon: FiMapPin },
  { label: 'My Profile', path: '/myaccount/profile', icon: FiUser },
];

const Profile = ({ orderId }) => {
  const { logout } = useAuth();
  const [activeMenu, setActiveMenu] = useState('Overview');
  const navigate = useNavigate();
  const location = useLocation();

  const pathToMenu = useMemo(() => ({
    '/myaccount': 'Overview',
    '/myaccount/orders': 'My Orders',
    '/myaccount/order': 'Order Details',
    '/myaccount/payments': 'My Payments',
    '/myaccount/wallet': 'My Wallet',
    '/myaccount/addresses': 'My Addresses',
    '/myaccount/profile': 'My Profile',
    '/logout': 'Logout',
  }), []);

  useEffect(() => {
    const menu = pathToMenu[location.pathname];
    if (menu) {
      setActiveMenu(menu);
    }
  }, [location.pathname, pathToMenu]);

  const renderContent = () => {
    switch (activeMenu) {
      case 'Overview':
        return <OverView />;
      case 'My Orders':
        return <Myorders />;
      case 'Order Details':
        return <OrderDetails orderId={orderId} />;
      case 'My Addresses':
        return <ShippingAddress />;
      case 'My Profile':
        return <MyProfile />;
      case 'Logout':
        logout();
        break;
      default:
        return <div>Select a menu item</div>;
    }
  };

  return (
    <MainLayOut>
      <ToastContainer position="top-center" />
      <div className="bg-gray-50 min-h-screen">
        {/* Mobile sub-page header — Overview itself acts as the mobile hub,
            everything else gets a lightweight back bar since the sidebar is
            desktop-only. */}
        {activeMenu !== 'Overview' && (
          <div className="md:hidden sticky top-0 z-30 bg-white border-b border-gray-100 flex items-center gap-3 px-4 py-3">
            <button
              onClick={() => navigate('/myaccount')}
              className="w-9 h-9 flex items-center justify-center rounded-full bg-gray-100 text-gray-700 shrink-0"
              aria-label="Back to account"
            >
              <FiChevronLeft size={20} />
            </button>
            <h1 className="text-base font-semibold text-gray-900">{activeMenu}</h1>
          </div>
        )}

        <div className="max-w-screen-2xl mx-auto px-0 sm:px-6 lg:px-8 py-0 md:py-10">
          <div className="flex gap-6">
            {/* Sidebar — desktop only */}
            <div className="w-64 shrink-0 hidden md:block">
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 sticky top-6">
                <nav className="space-y-1">
                  {NAV_ITEMS.map((item) => {
                    const ItemIcon = item.icon;
                    const isActive = activeMenu === item.label;
                    return (
                      <button
                        key={item.label}
                        onClick={() => {
                          setActiveMenu(item.label);
                          navigate(item.path);
                        }}
                        className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                          isActive
                            ? 'bg-brand-600 text-white'
                            : 'text-gray-600 hover:bg-gray-50'
                        }`}
                      >
                        <ItemIcon size={18} />
                        {item.label}
                      </button>
                    );
                  })}
                </nav>

                <div className="h-px bg-gray-100 my-3" />

                <button
                  onClick={() => navigate('/logout')}
                  className="w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium text-red-600 hover:bg-red-50 transition-colors"
                >
                  <FiLogOut size={18} />
                  Logout
                </button>
              </div>
            </div>

            {/* Main Content Area */}
            <div className="flex-1 min-w-0 pb-24 md:pb-0 px-4 md:px-0 pt-4 md:pt-0">
              {renderContent()}
            </div>
          </div>
        </div>
      </div>

      <BottomFixedNav />
    </MainLayOut>
  );
};

export default Profile;
